import { useState, useCallback } from 'react';
import moment from 'moment';
import i18n from "i18n-react";
import { modalForbidden } from "../components/DefaultAjax";
import { toastFunction } from '../components/mainFunctions';
import SecureAjax from '../utils/SecureAjax';

export const useMultimedia = ({ url, setSpinner, setModalContent, onUpdateStorage }) => {
    const [startDate, setStartDate] = useState(moment().add(-12, 'hour'));
    const [endDate, setEndDate] = useState(moment().add(12, 'hour'));
    const [multimediaList, setMultimediaList] = useState([]);
    const [multimediaChecked, setMultimediaChecked] = useState([]);
    const [checkAll, setCheckAll] = useState(false);
    const [offset, setOffset] = useState(0);
    const [perPage] = useState(8);
    const [currentPage, setCurrentPage] = useState(0);

    const extraStorageIndex = useCallback((customStartDate, customEndDate) => {
        let dto = {
            startDate: customStartDate || startDate,
            endDate: customEndDate || endDate
        };
        SecureAjax.get("multimedia/getAllMultimedia?paramsJSON=" + encodeURI(JSON.stringify(dto)))
            .then((res) => {
                if (res) {
                    let result = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
                    setMultimediaList(result);

                    if (document.querySelector("#inputCheckedAll")?.checked) {
                        setTimeout(() => {
                            setMultimediaChecked([...result]);
                            setCheckAll(true);
                        }, 100);
                    } else {
                        setMultimediaChecked([]);
                        setCheckAll(false);
                    }

                    setSpinner("");
                }
            }).catch((error) => {
                setSpinner("");
                if (error.response && error.response.status === 403) {
                    setTimeout(() => {
                        setModalContent(modalForbidden());
                    }, 10);
                }
            });
    }, [startDate, endDate, setSpinner, setModalContent]);

    const handlePageClick = useCallback((e) => {
        const selectedPage = e.selected;
        setOffset(selectedPage * perPage);
        setCurrentPage(selectedPage);
    }, [perPage]);

    const toggleCheckImage = useCallback((mList) => {
        setMultimediaChecked(prev => {
            const index = prev.findIndex(m => m.id === mList.id);
            let updated = [...prev];
            if (index > -1) {
                updated.splice(index, 1);
            } else {
                updated.push(mList);
            }
            setCheckAll(updated.length === multimediaList.length && updated.length > 0);
            return updated;
        });
    }, [multimediaList]);

    const toggleCheckAll = useCallback(() => {
        if (!checkAll) {
            setMultimediaChecked([...multimediaList]);
            setCheckAll(true);
        } else {
            setMultimediaChecked([]);
            setCheckAll(false);
        }
    }, [checkAll, multimediaList]);

    const deselectAll = useCallback(() => {
        setMultimediaChecked([]);
        setCheckAll(false);
    }, []);

    const handleResponseDownloadFile = useCallback((response) => {
        if (response.status === 200 || response.status === "200") {
            const FileDownload = require('js-file-download');
            toastFunction(i18n.translate("download_started"), "success");
            deselectAll();
            FileDownload(response.data, 'descarga.zip');
        } else {
            toastFunction(i18n.translate("an_error_has_ocurred"), "error");
        }
    }, [deselectAll]);

    const executeDownloadAllMultimedia = useCallback(() => {
        SecureAjax.get("multimedia/downloadAll", {
            responseType: 'blob'
        }).then(response => {
            handleResponseDownloadFile(response);
            setModalContent("");
            setSpinner("");
        }).catch((error) => {
            setSpinner("");
            if (error.response && error.response.status === 403) {
                setTimeout(() => {
                    setModalContent(modalForbidden());
                }, 10);
            }
        });
    }, [url, setSpinner, setModalContent, handleResponseDownloadFile]);

    const downloadImages = useCallback((ids) => {
        SecureAjax.get(`multimedia/downloadSelected?multimediaIdListJSON=${encodeURI(JSON.stringify(ids))}`, {
            responseType: 'blob'
        }).then(response => {
            setSpinner("");
            handleResponseDownloadFile(response);
        }).catch(error => {
            setSpinner("");
            if (error.response && error.response.status === 403) {
                setTimeout(() => {
                    setModalContent(modalForbidden());
                }, 10);
            }
        });
    }, [url, setSpinner, setModalContent, handleResponseDownloadFile]);

    const executeDeleteMultimedia = useCallback((dataToDelete) => {
        SecureAjax.delete(
            "multimedia/deleteSelected?multimediaIdListJSON=" + encodeURIComponent(JSON.stringify(dataToDelete))
        ).then(res => {
            if (res) {
                const idsToDelete = dataToDelete;
                const isNotDeleted = (media) => !idsToDelete.includes(media.id);
                setMultimediaList(prev => prev.filter(isNotDeleted));
                deselectAll();
                setSpinner("");
                setModalContent("");
                toastFunction(i18n.translate("image_deleted"), "success");
                if (onUpdateStorage) onUpdateStorage();
            } else {
                setSpinner("");
                toastFunction(i18n.translate("an_error_has_ocurred"), "error");
            }
        }).catch((error) => {
            setSpinner("");
            if (error.response && error.response.status === 403) {
                setTimeout(() => {
                    setModalContent(modalForbidden());
                }, 10);
            }
        });
    }, [setSpinner, setModalContent, deselectAll, onUpdateStorage]);

    return {
        startDate, setStartDate, endDate, setEndDate,
        multimediaList, multimediaChecked, checkAll,
        offset, perPage, currentPage,
        extraStorageIndex, handlePageClick,
        toggleCheckImage, toggleCheckAll, deselectAll,
        executeDownloadAllMultimedia, downloadImages, executeDeleteMultimedia
    };
};